import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { storage } from '@/utils/storage';
import {
  detectBrowserLocale,
  translateWithFallback,
  LEGACY_LOCALE_MAP,
  type SupportedLocale,
} from '@/i18n';

type Language = 'auto' | SupportedLocale;

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  effectiveLanguage: SupportedLocale;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = '@biavent_language';

/**
 * Obtiene el idioma efectivo basado en la preferencia y el navegador
 */
function getEffectiveLanguage(language: Language): SupportedLocale {
  if (language === 'auto') {
    return detectBrowserLocale();
  }
  return language;
}

const VALID_LANGUAGES = ['en', 'es', 'de', 'pt', 'fr', 'it', 'zh', 'ja', 'auto'] as const;

function normalizeLanguage(value: string): Language {
  if (value in LEGACY_LOCALE_MAP) {
    return LEGACY_LOCALE_MAP[value];
  }
  if (VALID_LANGUAGES.includes(value as (typeof VALID_LANGUAGES)[number])) {
    return value as Language;
  }
  return 'auto';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>('auto');
  const [effectiveLanguage, setEffectiveLanguage] = useState<SupportedLocale>(() => {
    // Inicializar con el idioma del navegador o localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored) {
        return getEffectiveLanguage(normalizeLanguage(stored));
      }
    }
    return getEffectiveLanguage('auto');
  });

  // Cargar idioma desde localStorage al montar
  useEffect(() => {
    const loadLanguage = async () => {
      try {
        const stored = await storage.getItem(LANGUAGE_STORAGE_KEY);
        if (stored) {
          const normalized = normalizeLanguage(stored);
          setLanguageState(normalized);
          const effective = getEffectiveLanguage(normalized);
          setEffectiveLanguage(effective);
          if (normalized !== stored) {
            await storage.setItem(LANGUAGE_STORAGE_KEY, normalized);
          }
        } else {
          // Usar idioma automático por defecto
          const effective = getEffectiveLanguage('auto');
          setEffectiveLanguage(effective);
        }
      } catch (error) {
        console.error('Error loading language:', error);
        const effective = getEffectiveLanguage('auto');
        setEffectiveLanguage(effective);
      }
    };

    loadLanguage();
  }, []);

  // Actualizar idioma efectivo cuando cambia la preferencia
  useEffect(() => {
    const effective = getEffectiveLanguage(language);
    setEffectiveLanguage(effective);
  }, [language]);

  const setLanguage = async (newLanguage: Language) => {
    try {
      setLanguageState(newLanguage);
      await storage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
      const effective = getEffectiveLanguage(newLanguage);
      setEffectiveLanguage(effective);
    } catch (error) {
      console.error('Error saving language:', error);
    }
  };

  // Función de traducción
  const t = (key: string): string => translateWithFallback(effectiveLanguage, key);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, effectiveLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
